import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartLogo } from '../components/hbs/HeartLogo';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { HbsStrengthMeter } from '../components/hbs/StrengthMeter';
import { EyeToggle } from '../components/hbs/EyeToggle';
import { SafePasswordTipsModal } from '../components/SafePasswordTipsModal';
import { passwordStrength, MIN_PASSWORD_LENGTH } from '@heybesafe/shared';
import * as opaque from '@serenity-kit/opaque';
import {
  recoveryFinish,
  recoveryLookup,
  recoveryOpaqueStart,
  recoveryRequest,
  recoveryRequestCode,
  recoveryStatus,
  storeTokens,
} from '../api/client';
import type { RecoveryContactOption } from '@heybesafe/shared';
import { unwrapForResetEphemeral, ephemeralWords } from '@heybesafe/shared';
import {
  deriveAccountWrappingKey,
  generateAccountKeyPair,
  localWrapAccountPrivateKey,
  wrapAccountPrivateKey,
} from '../crypto/familyKeys';
import { setLocalWrappedAccountKey, setMeta } from '../storage/safe-store';
import { markAccountUsed } from '../lib/firstRun';
import {
  deriveLocalWrappingKey,
  deriveWrappingKeyFromExportKey,
  generateLocalKdfSalt,
  wrapVaultKey,
} from '../crypto/crypto';
import { bytesToBase64Url, base64UrlToBytes } from '../crypto/base64url';

// The recovery requester never sees a contact's full email. On email submit we
// (1) POST /auth/recovery/request-code, which emails an 8-digit possession code
// to the ACCOUNT address (proving inbox control), and (2) POST
// /auth/recovery/lookup, which returns an opaque, short-TTL `contactHandle` per
// contact plus a server-masked email. We carry the code + the chosen
// contactHandle through the steps; `recoveryRequest` requires both. maskedEmail
// is display-only.
type Step =
  | { kind: 'email' }
  | { kind: 'code'; email: string; contacts: RecoveryContactOption[] }
  | { kind: 'pick-contact'; email: string; code: string; contacts: RecoveryContactOption[] }
  | { kind: 'new-password'; email: string; code: string; contacts: RecoveryContactOption[];
      contactHandle: string; maskedEmail: string }
  | { kind: 'waiting'; email: string; maskedEmail: string;
      newPassword: string;
      ephPrivReset: Uint8Array;
      ephPubReset: string;
      recoverySessionToken: string;
      verificationCode: string }
  | { kind: 'success' };

export default function ForgotPasswordPage(): JSX.Element {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  return (
    <main className="min-h-screen bg-hbs-bg flex items-center justify-center p-6">
      <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-8 shadow-hbs-lg max-w-sm w-full">
        <div className="flex flex-col items-center gap-3 mb-6">
          <HeartLogo size={56} />
          <span className="font-display font-semibold text-[18px] tracking-hbs-tight text-hbs-text">
            Hey Be Safe
          </span>
        </div>
        <h1 className="text-2xl font-display font-semibold text-hbs-text mb-2">
          Forgot your safe password?
        </h1>
        <p className="text-sm text-hbs-text-mute mb-6">
          If you've designated a recovery contact, they can help you reset your
          password without losing your safe.
        </p>
        {step.kind === 'email' && <EmailStep
          submitting={submitting} error={error}
          onSubmit={async (email) => {
            setSubmitting(true); setError(null);
            // Email the 8-digit possession code (enumeration-safe — identical
            // response whether or not the account exists) and fetch the recovery
            // contacts in parallel.
            const [, r] = await Promise.all([
              recoveryRequestCode({ email }),
              recoveryLookup({ email }),
            ]);
            setSubmitting(false);
            if (!r.ok) { setError(r.error); return; }
            if (r.data.contacts.length === 0) {
              setError('No recovery contacts found for this email. If you didn\'t designate one, your safe cannot be recovered.');
              return;
            }
            setStep({ kind: 'code', email, contacts: r.data.contacts });
          }}
        />}
        {step.kind === 'code' && <CodeStep
          email={step.email}
          error={error}
          onSubmit={(code) => {
            setError(null);
            const contacts = step.contacts;
            // One contact: skip the picker. Otherwise let the user choose.
            if (contacts.length === 1) {
              const only = contacts[0];
              setStep({ kind: 'new-password', email: step.email, code, contacts,
                contactHandle: only.contactHandle, maskedEmail: only.maskedEmail });
            } else {
              setStep({ kind: 'pick-contact', email: step.email, code, contacts });
            }
          }}
          onBack={() => { setError(null); setStep({ kind: 'email' }); }}
        />}
        {step.kind === 'pick-contact' && <PickContactStep
          email={step.email}
          contacts={step.contacts}
          onPick={(contactHandle, maskedEmail) => setStep({
            kind: 'new-password', email: step.email, code: step.code, contacts: step.contacts,
            contactHandle, maskedEmail,
          })}
          onBack={() => setStep({ kind: 'code', email: step.email, contacts: step.contacts })}
        />}
        {step.kind === 'new-password' && <NewPasswordStep
          email={step.email}
          maskedEmail={step.maskedEmail}
          submitting={submitting} error={error}
          onSubmit={async (newPassword) => {
            setSubmitting(true); setError(null);
            try {
              // 1. Generate ephemeral X25519 keypair.
              const eph = await generateAccountKeyPair();
              // 2. Encode ephPub as base64url for the API.
              const ephPubReset = bytesToBase64Url(eph.publicKey);
              // 3. POST /auth/recovery/request — the emailed code + the opaque
              //    contactHandle (no raw contactId, no email of the contact).
              const r = await recoveryRequest({
                email: step.email, code: step.code, contactHandle: step.contactHandle, ephPubReset,
              });
              if (!r.ok) {
                setError(r.error); setSubmitting(false); return;
              }
              // 4. Move to waiting step.
              setStep({
                kind: 'waiting',
                email: step.email, maskedEmail: step.maskedEmail,
                newPassword, ephPrivReset: eph.privateKey, ephPubReset,
                recoverySessionToken: r.data.recoverySessionToken,
                verificationCode: r.data.verificationCode,
              });
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Failed to start recovery');
            } finally {
              setSubmitting(false);
            }
          }}
          // Back to the code step so a rejected code (or a re-pick) can be
          // corrected without re-entering the email.
          onBack={() => setStep({ kind: 'code', email: step.email, contacts: step.contacts })}
        />}
        {step.kind === 'waiting' && <WaitingStep
          email={step.email}
          maskedEmail={step.maskedEmail}
          newPassword={step.newPassword}
          ephPrivReset={step.ephPrivReset}
          ephPubReset={step.ephPubReset}
          recoverySessionToken={step.recoverySessionToken}
          verificationCode={step.verificationCode}
          error={error}
          onSuccess={() => setStep({ kind: 'success' })}
          onError={(msg) => setError(msg)}
        />}
        {step.kind === 'success' && <SuccessStep />}
        <div className="mt-6 text-sm text-hbs-text-mute">
          <Link to="/auth" className="hover:text-hbs-text">← Back to sign in</Link>
        </div>
      </div>
    </main>
  );
}

function EmailStep({
  submitting, error, onSubmit,
}: {
  submitting: boolean; error: string | null;
  onSubmit: (email: string) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(email); }} className="flex flex-col gap-4">
      <HbsInput
        label="Email associated with your Hey Be Safe account"
        type="email"
        value={email}
        onChange={setEmail}
        required
        autoFocus
        name="forgot-email"
      />
      {error && (
        <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3" role="alert">
          {error}
        </p>
      )}
      <HbsButton
        id="forgot-email-continue-button"
        type="submit"
        disabled={!email.trim()}
        loading={submitting}
        fullWidth
      >
        Continue
      </HbsButton>
    </form>
  );
}

function CodeStep({
  email, error, onSubmit, onBack,
}: {
  email: string; error: string | null;
  onSubmit: (code: string) => void;
  onBack: () => void;
}): JSX.Element {
  const [code, setCode] = useState('');
  const digits = code.replace(/\D/g, '').slice(0, 8);
  const complete = digits.length === 8;
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (complete) onSubmit(digits); }} className="flex flex-col gap-4">
      <p className="text-sm text-hbs-text-mute">
        We emailed an 8-digit code to <span className="text-hbs-text">{email}</span>.
        Enter it to confirm this is your account. It expires in 10 minutes.
      </p>
      <HbsInput
        label="8-digit code"
        type="tel"
        value={digits}
        onChange={(v) => setCode(v)}
        required
        autoFocus
        mono
        name="forgot-code"
      />
      {error && (
        <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2 items-center">
        <HbsButton id="forgot-code-back-button" type="button" variant="plain" onClick={onBack}>
          ← Different email
        </HbsButton>
        <div className="flex-1">
          <HbsButton id="forgot-code-continue-button" type="submit" disabled={!complete} fullWidth>
            Continue
          </HbsButton>
        </div>
      </div>
    </form>
  );
}

function PickContactStep({
  email, contacts, onPick, onBack,
}: {
  email: string; contacts: RecoveryContactOption[];
  onPick: (contactHandle: string, maskedEmail: string) => void;
  onBack: () => void;
}): JSX.Element {
  return (
    <div>
      <p className="text-sm text-hbs-text-mute mb-3">
        Who would you like to ask for help recovering <span className="text-hbs-text">{email}</span>?
      </p>
      <ul className="flex flex-col gap-1 mb-4">
        {contacts.map((c) => (
          <li key={c.contactHandle}>
            <button
              type="button"
              id={`forgot-pick-contact-${c.contactHandle}`}
              onClick={() => onPick(c.contactHandle, c.maskedEmail)}
              className="w-full text-left p-3 rounded border border-hbs-border hover:border-hbs-primary bg-hbs-surface-alt hover:bg-hbs-primary/10"
            >
              {/* maskedEmail is server-rendered (web/src/api/apiBaseUrl.ts +
                  shared/src/maskEmail.ts) so the requester never receives
                  the full email of a contact they didn't already know. */}
              <div className="text-sm font-medium text-hbs-text">{c.maskedEmail}</div>
            </button>
          </li>
        ))}
      </ul>
      <HbsButton id="forgot-pick-contact-back-button" type="button" variant="plain" onClick={onBack}>
        ← Different email
      </HbsButton>
    </div>
  );
}

function NewPasswordStep({
  email, maskedEmail, submitting, error, onSubmit, onBack,
}: {
  email: string; maskedEmail: string;
  submitting: boolean; error: string | null;
  onSubmit: (newPassword: string) => void;
  onBack: () => void;
}): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  // Strong-password tips: pops once on first focus of the New password field,
  // matching the AuthPage + ChangePasswordModal flows.
  const [tipsOpen, setTipsOpen] = useState(false);
  const [tipsDismissed, setTipsDismissed] = useState(false);

  const tooShort = password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const submitDisabled = submitting || tooShort || password !== confirmPassword;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(password); }}
      className="flex flex-col gap-4"
    >
      <p className="text-sm text-hbs-text-mute">
        Set a new safe password for <span className="text-hbs-text">{email}</span>.
        Once <span className="text-hbs-text">{maskedEmail}</span> approves the request,
        you'll be signed in with this new password.
      </p>

      <div>
        <HbsInput
          label="New safe password"
          type={revealed ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          required
          autoFocus
          mono={revealed}
          name="forgot-newpw"
          onFocus={() => {
            if (!tipsDismissed) setTipsOpen(true);
          }}
          trailingActions={<EyeToggle revealed={revealed} onChange={setRevealed} />}
        />
        {password.length > 0 && (
          <div className="mt-2">
            <HbsStrengthMeter strength={passwordStrength(password)} />
          </div>
        )}
      </div>

      <HbsInput
        label="Confirm new safe password"
        type="password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        required
        name="forgot-newpw-confirm"
        error={mismatch ? 'Passwords do not match' : undefined}
      />

      {error && (
        <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-2 mt-2 items-center">
        <HbsButton id="forgot-help-back-button" type="button" variant="plain" onClick={onBack}>
          ← Back
        </HbsButton>
        <div className="flex-1">
          <HbsButton
            id="forgot-request-help-button"
            type="submit"
            disabled={submitDisabled}
            loading={submitting}
            fullWidth
          >
            Request help
          </HbsButton>
        </div>
      </div>

      <SafePasswordTipsModal
        open={tipsOpen}
        onClose={() => {
          setTipsOpen(false);
          setTipsDismissed(true);
        }}
      />
    </form>
  );
}

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Waiting step: polls `/auth/recovery/status` every 5s until the contact has
 * approved (`RESET_APPROVED`), then runs OPAQUE re-registration with the new
 * password, re-wraps the recovered keys for storage, posts `/auth/recovery/finish`,
 * and stores the Cognito tokens — mirroring the post-login bootstrap in
 * `SafeContext.register/login`. On the next page render, `SafeContext`'s
 * initial-load effect will pick up the new `localKdfSalt` + tokens and the
 * user lands on `/safe` in the locked state (or unlocked, after a one-time
 * unlock with the new password).
 *
 * The OPAQUE crypto + key wrapping mirror `SafeContext.register` (lines 435-466).
 * The recovery-specific differences:
 *   - the vault key is RECOVERED (decrypted from the contact-uploaded package),
 *     not generated fresh
 *   - the account private key is ALSO recovered (so the user keeps cross-admin
 *     family-key access without re-bootstrap)
 *   - the wraps are packed as base64(JSON({wrapped, iv})) for the recovery API,
 *     matching the contact-side approve step's encoding in RecoveryContactsPage
 *   - the finish POSTs `/auth/recovery/finish` (not `/auth/opaque/register/finish`)
 */
function WaitingStep({
  email, maskedEmail, newPassword, ephPrivReset, ephPubReset, recoverySessionToken, verificationCode,
  error, onSuccess, onError,
}: {
  email: string; maskedEmail: string; newPassword: string;
  ephPrivReset: Uint8Array; ephPubReset: string; recoverySessionToken: string; verificationCode: string;
  error: string | null;
  onSuccess: () => void; onError: (msg: string) => void;
}): JSX.Element {
  const finalizingRef = useRef(false);
  const [phase, setPhase] = useState<'waiting' | 'finalizing'>('waiting');
  // PKA A5: fingerprint of our ephemeral public key. We read this to the contact
  // out-of-band (same channel as the 6-digit code) so they can confirm the
  // server didn't substitute the ephemeral before wrapping our recovered keys.
  const [ephFp, setEphFp] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    void ephemeralWords(base64UrlToBytes(ephPubReset)).then((fp) => { if (!cancelled) setEphFp(fp); });
    return () => { cancelled = true; };
  }, [ephPubReset]);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    async function poll(): Promise<void> {
      while (!cancelled && Date.now() - startedAt < POLL_TIMEOUT_MS) {
        const r = await recoveryStatus(recoverySessionToken);
        if (cancelled) return;
        if (!r.ok) {
          onError(r.error);
          return;
        }
        if (r.data.state === 'RESET_APPROVED'
            && r.data.resetWrappedVaultKey
            && r.data.resetWrappedAccountPrivateKey) {
          if (finalizingRef.current) return;
          finalizingRef.current = true;
          setPhase('finalizing');
          await finalize(r.data.resetWrappedVaultKey, r.data.resetWrappedAccountPrivateKey);
          return;
        }
        // Anything other than RESET_REQUESTED means the contact denied (state
        // transitions back to ACTIVE) or the request was abandoned. Stop the
        // poll loop — see server/lib/recoveryStateMachine.ts for the
        // reset-deny transition.
        if (r.data.state !== 'RESET_REQUESTED') {
          onError('Your recovery contact declined the request, or the request expired. Start again to send a new one.');
          return;
        }
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
      }
      if (!cancelled) {
        onError('Recovery timed out. Start again to send a new request.');
      }
    }

    async function finalize(
      wrappedVaultKeyBase64: string,
      wrappedAccountKeyBase64: string,
    ): Promise<void> {
      try {
        // 1. Decrypt the ephemeral package with ephPrivReset.
        //    The contact wraps both bundles via `wrapForResetEphemeral` and
        //    encodes them as base64(JSON(bundle)) — see RecoveryContactsPage.tsx
        //    approveReset(). Mirror that decoding here.
        const vaultBundle = JSON.parse(atob(wrappedVaultKeyBase64));
        const accountBundle = JSON.parse(atob(wrappedAccountKeyBase64));
        const recoveredVaultKeyBytes = await unwrapForResetEphemeral(
          vaultBundle, ephPrivReset, 'recovery-reset-vault-key-v1',
        );
        const recoveredAccountPrivateKey = await unwrapForResetEphemeral(
          accountBundle, ephPrivReset, 'recovery-reset-account-key-v1',
        );

        // 2. OPAQUE re-registration with the new password.
        //    /auth/opaque/register/start can't be used here — it 409s with
        //    EMAIL_EXISTS for any already-registered email, which is exactly
        //    the situation in a password reset. /auth/recovery/opaque-start
        //    runs the same createRegistrationResponse but is gated by the
        //    recoverySessionToken + a RESET_APPROVED row state. The finalize
        //    then posts the resulting registrationRecord to
        //    /auth/recovery/finish (already recovery-specific).
        await opaque.ready;
        const { clientRegistrationState, registrationRequest } =
          opaque.client.startRegistration({ password: newPassword });
        const startResp = await recoveryOpaqueStart(
          recoverySessionToken, { registrationRequest },
        );
        if (!startResp.ok) {
          onError(startResp.error);
          return;
        }
        const { registrationRecord, exportKey } = opaque.client.finishRegistration({
          clientRegistrationState,
          registrationResponse: startResp.data.registrationResponse,
          password: newPassword,
        });

        // 3. Re-wrap the recovered vault key under HKDF(new exportKey) and
        //    the recovered account private key under the fresh PBKDF2-derived
        //    local wrapping key. Same shape as SafeContext.register's
        //    afterAuth bootstrap.
        const recoveredVaultKey = await crypto.subtle.importKey(
          'raw', recoveredVaultKeyBytes as BufferSource,
          { name: 'AES-GCM' }, true, ['encrypt', 'decrypt'],
        );
        const exportWrappingKey = await deriveWrappingKeyFromExportKey(exportKey);
        const { wrapped: vkWrapped, iv: vkIv } =
          await wrapVaultKey(recoveredVaultKey, exportWrappingKey);

        const newLocalKdfSalt = generateLocalKdfSalt();
        const localWrappingKey = await deriveLocalWrappingKey(
          newPassword, email, newLocalKdfSalt,
        );

        // The server's wrappedAccountPrivateKey must be encrypted under the
        // accountWrappingKey (HKDF from the OPAQUE exportKey) — that's the
        // wrap SafeContext.afterAuth reads via getAccountKey() after a full
        // login. Wrapping it under localWrappingKey here looked superficially
        // fine but would silently break account-key recovery on the next
        // OPAQUE re-login (refresh-token expiry path).
        const accountWrappingKey = await deriveAccountWrappingKey(exportKey);
        const { ciphertext: apkCt, iv: apkIv } =
          await wrapAccountPrivateKey(recoveredAccountPrivateKey, accountWrappingKey);

        // ALSO compute the local wrap of the same private key — that's what
        // SafeContext.unlock() reads from `hbs:localWrappedAccountKey` to
        // bootstrap AccountKeyContext. Without this, family-key decrypt
        // silently fails on the next unlock and the family safe appears empty.
        const localAccountWrap = await localWrapAccountPrivateKey(
          recoveredAccountPrivateKey, localWrappingKey,
        );

        // Also wrap the recovered vault key under localWrappingKey — that's
        // the wrap SafeContext.unlock() reads from `hbs:localWrappedSafeKey`
        // (vkWrapped above is under exportWrappingKey, which is the server-
        // side wrap). Without this local wrap, SafeContext's initial-load
        // effect sees empty meta and routes to /auth instead of /safe.
        const { wrapped: localWrappedVaultKey, iv: localWrappedVaultKeyIv } =
          await wrapVaultKey(recoveredVaultKey, localWrappingKey);

        // 4. POST /auth/recovery/finish — server commits the new opaqueRecord,
        //    vault-key wrap, account-key wrap, and KDF salt, then issues a fresh
        //    Cognito session. Wrap + IV are posted as separate fields, matching
        //    the four PROFILE columns the registration flow writes and the
        //    login flow reads (encryptedVaultKey / encryptedVaultKeyIv /
        //    wrappedAccountPrivateKey / wrappedAccountPrivateKeyIv). An earlier
        //    version packed them into `base64(JSON({wrapped, iv}))` blobs in
        //    the wrap field and left the IV column stale — the next OPAQUE
        //    login then threw OperationError on the AES-GCM unwrap.
        const fr = await recoveryFinish(recoverySessionToken, {
          newOpaqueRecord: registrationRecord,
          newEncryptedVaultKey: vkWrapped,
          newEncryptedVaultKeyIv: vkIv,
          newWrappedAccountPrivateKey: apkCt,
          newWrappedAccountPrivateKeyIv: apkIv,
          newLocalKdfSalt,
        });
        if (!fr.ok) {
          onError(fr.error);
          return;
        }

        // 5. Store tokens via the existing helper used by register/login
        //    (sessionStorage for id/access/userId, localStorage for refresh).
        await storeTokens(fr.data);

        // 6. Populate the local-vault meta — the same writes
        //    SafeContext.afterAuth() performs after register/login. Without
        //    these, SafeContext's initial-load effect sees missing meta and
        //    enters `unauthenticated`, so AuthGuard bounces the user back to
        //    /auth even with a valid refresh token. Pattern A from the
        //    Phase 2 plan: writes-then-navigate; user lands at the locked
        //    screen with email pre-filled and unlocks once with the new
        //    password.
        setMeta('email', email);
        setMeta('localKdfSalt', newLocalKdfSalt);
        setMeta('localWrappedSafeKey', localWrappedVaultKey);
        setMeta('localWrappedSafeKeyIv', localWrappedVaultKeyIv);
        // Record the account signal so a later logout (which wipes hbs:email)
        // doesn't show this user "Create your safe" — see firstRun.ts. Without
        // this, someone who just fought through account recovery would be told
        // their account doesn't exist.
        markAccountUsed();
        // Local wrap of the account private key — SafeContext.unlock reads
        // this to bootstrap AccountKeyContext (which family-key decrypt
        // depends on). Without it the family safe appears empty after
        // recovery even though the user is otherwise signed in.
        setLocalWrappedAccountKey(localAccountWrap.ciphertext, localAccountWrap.iv);

        if (!cancelled) onSuccess();
      } catch (e) {
        if (!cancelled) {
          onError(e instanceof Error ? e.message : 'Failed to finalize recovery');
        }
      }
    }

    void poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoverySessionToken]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Spinner />
        <p className="text-sm text-hbs-text">
          {phase === 'finalizing'
            ? 'Approval received — setting up your new password…'
            : `Waiting for ${maskedEmail} approval…`}
        </p>
      </div>
      <p className="text-xs text-hbs-text-mute">
        {phase === 'finalizing'
          ? "Hang tight — encrypting your new credentials and signing you in."
          : "They'll receive an email asking them to confirm. This may take a few minutes. You can leave this page open — it'll automatically continue once they approve."}
      </p>
      {phase !== 'finalizing' && (
        <div className="mt-4 rounded-hbs-md border border-hbs-border bg-hbs-surface-alt p-3">
          <p className="text-xs text-hbs-text-mute mb-1">
            Read this code to <span className="text-hbs-text">{maskedEmail}</span> — they must enter
            it to approve. Only share it with them directly.
          </p>
          <p className="font-mono tracking-hbs-mono text-2xl text-hbs-text" aria-label="Verification code">
            {verificationCode}
          </p>
          <p className="text-xs text-hbs-text-mute mt-3 mb-1">
            Also read them these six security words so they can confirm no one tampered
            with your request:
          </p>
          <ol
            className="grid grid-rows-3 grid-flow-col gap-x-6 gap-y-1 mt-1"
            aria-label="Reset security words"
            data-testid="reset-fingerprint"
          >
            {(ephFp ?? []).map((w, i) => (
              <li key={w} className="flex items-baseline gap-2">
                <span className="text-xs text-hbs-text-mute tabular-nums w-4 shrink-0">{i + 1}.</span>
                <span className="font-mono text-sm text-hbs-text">{w}</span>
              </li>
            ))}
            {!ephFp && <li className="text-sm text-hbs-text-mute">Loading…</li>}
          </ol>
        </div>
      )}
      {error && (
        <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3 mt-3" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function SuccessStep(): JSX.Element {
  useEffect(() => {
    // Hard reload (not SPA navigate): the finalize step wrote the local-vault
    // meta to localStorage, but SafeProvider's initial-load effect runs ONCE
    // at app mount with `[]` deps and has already settled on `unauthenticated`
    // for this tab. A `navigate('/safe')` would hit AuthGuard with stale
    // status and bounce through /auth. `window.location.assign` re-mounts
    // SafeProvider, which re-runs the effect, sees the freshly-written meta +
    // refreshToken, and sets status='locked' — so the user lands at
    // UnlockPage and unlocks once with their new password.
    const t = setTimeout(() => window.location.assign('/safe'), 1500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="text-center py-4">
      <p className="text-sm text-hbs-text mb-2">✓ Signed in successfully.</p>
      <p className="text-xs text-hbs-text-mute">Redirecting to your safe…</p>
    </div>
  );
}

function Spinner(): JSX.Element {
  return (
    <div
      className="w-4 h-4 border-2 border-hbs-primary border-t-transparent rounded-full animate-spin"
      aria-hidden="true"
    />
  );
}
